/**
 * App-wide state: the active DataSource, the user's profile and diet settings, the selected
 * diary day, a "data changed" version counter, toasts, and the energy picture shared by every
 * page: body composition, BMR, the daily-burn context and the typical (planning) burn.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import type { DataSource, SettingsResult, TrajectoryResponse } from '../data/source';
import { bodyCompFrom, type BodyComp } from '../lib/bodyComp';
import { addDays, ageOn, todayIn } from '../lib/dates';
import {
  activityByDay, bmrOptions, chooseBmr, dayBurn, kcalPerStep, palFor, planningBurn,
  type BmrChoice, type BmrOptions, type DayBurn, type EnergyContext, type Observed, type PlanningBurn,
} from '../lib/energy';
import { errorMessage, log } from '../lib/logger';
import { readTefSettings, tefMacrosOfMeal, tefOfMeals, tefSettingsFrom, type MealsTef, type TefSettings } from '../lib/tef';
import { DEFAULT_SETTINGS, type ActivityDay, type BodyStat, type DailyMetric, type DietSettings, type MealLog, type Profile } from '../lib/types';

export interface Toast { id: number; text: string; kind: 'ok' | 'err' }

export interface EnergyState {
  ctx: EnergyContext;
  comp: BodyComp;
  bmrOptions: BmrOptions;
  bmr: BmrChoice | null;
  planning: PlanningBurn;
  tefSettings: TefSettings;
  ageYears: number | null;
  /** Watch days of the last 60 days, both sources, oldest first. */
  activityRows: ActivityDay[];
  observed: Observed | null;
  loading: boolean;
}

interface AppState {
  source: DataSource;
  profile: Profile;
  setProfile: (p: Profile) => void;
  settings: DietSettings;
  settingsStored: 'server' | 'device';
  saveSettings: (patch: Partial<DietSettings>) => Promise<DietSettings>;
  today: string;
  day: string;
  setDay: (d: string) => void;
  version: number;
  bump: () => void;
  toast: (text: string, kind?: 'ok' | 'err') => void;
  toasts: Toast[];
  energy: EnergyState;
  trajectory: TrajectoryResponse | null;
  trajectoryError: string | null;
  /** The site owner's account (app_admins): sees the Owner dashboard. */
  isAdmin: boolean;
  /** Submissions waiting for review (owner only; null until loaded). */
  pendingReviews: number | null;
  refreshReviews: () => void;
}

const Ctx = createContext<AppState | null>(null);

/** Several rows often change together (a meal and its day's totals); refresh once. */
const LIVE_DEBOUNCE_MS: number = 400;
const REFRESH_AFTER_HIDDEN_MS: number = 60_000;

export function useApp(): AppState {
  const v: AppState | null = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside <AppProvider>.');
  return v;
}

export function AppProvider({ source, initialProfile, children }: { source: DataSource; initialProfile: Profile; children: ReactNode }): ReactElement {
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const today: string = todayIn(profile.timezone);
  const [day, setDay] = useState<string>(today);
  const [version, setVersion] = useState<number>(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [settingsResult, setSettingsResult] = useState<SettingsResult>({ settings: DEFAULT_SETTINGS, stored: 'server' });
  const [trajectory, setTrajectory] = useState<TrajectoryResponse | null>(null);
  const [trajectoryError, setTrajectoryError] = useState<string | null>(null);
  const [stats, setStats] = useState<BodyStat[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityDay[]>([]);
  const [metrics, setMetrics] = useState<DailyMetric[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const toastId = useRef<number>(0);

  const bump = useCallback((): void => setVersion((v) => v + 1), []);
  const toast = useCallback((text: string, kind: 'ok' | 'err' = 'ok'): void => {
    const id: number = ++toastId.current;
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'err' ? 6500 : 3200);
  }, []);

  // Live updates: a change made on the phone app or in another tab refreshes every page here.
  // Coming back to the tab (or back online) after a while also refreshes, which covers the profile
  // and anything a dropped connection missed.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let hiddenAt: number | null = null;
    const unsubscribe: () => void = source.subscribeChanges((table) => {
      log.info('live.change', { table });
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; bump(); }, LIVE_DEBOUNCE_MS);
    });
    const refresh = (): void => {
      bump();
      source.getProfile().then(setProfile).catch((err: unknown) => log.warn('live.profile_refresh_failed', { error: errorMessage(err) }));
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt > REFRESH_AFTER_HIDDEN_MS) refresh();
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', refresh);
    return () => {
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', refresh);
    };
  }, [source, bump]);

  // Settings (fast, and needed by every page).
  useEffect(() => {
    let cancelled: boolean = false;
    source.getSettings()
      .then((r) => { if (!cancelled) setSettingsResult(r); })
      .catch((err: unknown) => log.warn('settings.load_failed', { error: errorMessage(err) }));
    return () => { cancelled = true; };
  }, [source, version]);

  // Owner: the review badge. Checked once per sign-in, recounted on demand.
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [pendingReviews, setPendingReviews] = useState<number | null>(null);
  const [reviewsVersion, setReviewsVersion] = useState<number>(0);
  const refreshReviews = useCallback((): void => setReviewsVersion((v) => v + 1), []);
  useEffect(() => {
    let cancelled: boolean = false;
    source.isAdmin()
      .then(async (admin) => {
        if (cancelled) return;
        setIsAdmin(admin);
        if (!admin) return;
        const waiting = await source.listSubmissions('pending');
        if (!cancelled) setPendingReviews(waiting.length);
      })
      .catch((err: unknown) => log.warn('owner.check_failed', { error: errorMessage(err) }));
    return () => { cancelled = true; };
  }, [source, reviewsVersion]);

  const saveSettings = useCallback(async (patch: Partial<DietSettings>): Promise<DietSettings> => {
    const r: SettingsResult = await source.saveSettings(patch);
    setSettingsResult(r);
    return r.settings;
  }, [source]);

  // Weigh-ins, watch days and recent totals, then the trajectory (observed burn).
  useEffect(() => {
    let cancelled: boolean = false;
    (async (): Promise<void> => {
      const [s, a, m] = await Promise.allSettled([
        source.listBodyStats(addDays(today, -365), today),
        source.listActivityDays(addDays(today, -60), today),
        source.listDailyMetrics(addDays(today, -21), today),
      ]);
      if (cancelled) return;
      let latest: BodyStat[] = [];
      if (s.status === 'fulfilled') { latest = s.value; setStats(s.value); } else log.warn('energy.body_stats_failed', { error: errorMessage(s.reason) });
      if (a.status === 'fulfilled') setActivityRows(a.value); else log.warn('energy.activity_failed', { error: errorMessage(a.reason) });
      if (m.status === 'fulfilled') setMetrics(m.value); else log.warn('energy.metrics_failed', { error: errorMessage(m.reason) });
      setLoading(false);
      // A body-fat measurement taken later in the day than the morning weigh-in isn't in daily_metrics; pass it along.
      const comp: BodyComp = bodyCompFrom(latest, profile.body_fat_percent);
      const current = comp.leanMeasured ? { leanMassKg: comp.leanMassKg } : comp.bodyFatPct !== null ? { bodyFatPct: comp.bodyFatPct } : null;
      try {
        const t: TrajectoryResponse = await source.computeTrajectory({ lookbackDays: 42, horizonDays: 180, current });
        if (!cancelled) { setTrajectory(t); setTrajectoryError(null); }
      } catch (err) {
        const msg: string = errorMessage(err);
        log.info('energy.trajectory_unavailable', { error: msg });
        if (!cancelled) { setTrajectory(null); setTrajectoryError(msg); }
      }
    })();
    return () => { cancelled = true; };
  }, [source, today, version, profile.body_fat_percent]);

  const settings: DietSettings = settingsResult.settings;

  const energy: EnergyState = useMemo((): EnergyState => {
    const comp: BodyComp = bodyCompFrom(stats, profile.body_fat_percent);
    const ageYears: number | null = profile.birth_date ? ageOn(profile.birth_date, today) : null;
    const options: BmrOptions = bmrOptions(profile, comp, today, settings.bmr_custom_kcal);
    const bmr: BmrChoice | null = chooseBmr(settings.bmr_mode, options);
    const e = trajectory?.energy;
    const observed: Observed | null = e && e.tdeeKcal !== null && (e.tdeeStatus === 'ok' || e.tdeeStatus === 'low_confidence')
      ? { kcal: e.tdeeKcal, low: e.tdee95Low, high: e.tdee95High, status: e.tdeeStatus }
      : null;
    const ctx: EnergyContext = {
      settings, bmr, pal: palFor(profile.activity_level), kcalPerStep: kcalPerStep(comp.weightKg, profile.height_cm, profile.sex),
      rows: activityByDay(activityRows), observed, today,
    };
    // Synced choices win; a browser that set them before they synced keeps its own until then.
    const tefSettings: TefSettings = Object.keys(settings.tef_profile).length > 0 ? tefSettingsFrom(settings.tef_profile) : readTefSettings();
    const planning: PlanningBurn = planningBurn(ctx, metrics, tefSettings, ageYears);
    return { ctx, comp, bmrOptions: options, bmr, planning, tefSettings, ageYears, activityRows, observed, loading };
  }, [stats, profile, today, settings, trajectory, activityRows, metrics, loading]);

  const value: AppState = {
    source, profile, setProfile, settings, settingsStored: settingsResult.stored, saveSettings,
    today, day, setDay, version, bump, toast, toasts, energy, trajectory, trajectoryError,
    isAdmin, pendingReviews, refreshReviews,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The burn for one day with that day's food (TEF comes from the meals). */
export function useDayBurn(day: string, meals: ReadonlyArray<MealLog>): { burn: DayBurn; tef: MealsTef } {
  const { energy } = useApp();
  return useMemo(() => {
    const tef = tefOfMeals(meals.map(tefMacrosOfMeal), energy.tefSettings, energy.ageYears);
    return { burn: dayBurn(energy.ctx, day, tef.kcal), tef };
  }, [energy, day, meals]);
}

/** Runs an async loader and re-runs it when deps change. */
export function useAsync<T>(loader: () => Promise<T>, deps: ReadonlyArray<unknown>): { data: T | null; error: string | null; loading: boolean } {
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({ data: null, error: null, loading: true });
  useEffect(() => {
    let cancelled: boolean = false;
    setState((s) => ({ ...s, loading: true }));
    loader()
      .then((data) => { if (!cancelled) setState({ data, error: null, loading: false }); })
      .catch((err: unknown) => {
        const msg: string = errorMessage(err);
        log.error('load_failed', { error: msg });
        if (!cancelled) setState({ data: null, error: msg, loading: false });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}
